/**
 * Escritas da página do setor. Cada ação chama o service, aplica a resposta
 * no estado na hora (useEstadoMapa.aplicarAlocacao/aplicarTarefa/aplicarPeca/
 * aplicarSetor) e pede uma leitura nova — o que outro gestor mudou no meio do
 * caminho também chega.
 *
 * 409 (versão divergente ou transição que não vale no estado atual) vira o
 * aviso da página, com a mensagem do backend e o botão Recarregar; o erro
 * segue para quem chamou tratar do seu jeito (mensagem no painel, toast).
 */
import { useCallback, useMemo, useState } from "react";

import {
  alocarAmbiente,
  alocarPeca,
  atualizarColaborador,
  atualizarPeca,
  atualizarPosto,
  atualizarSetor,
  atualizarSubsetor,
  atualizarTarefa,
  comentarPeca,
  comentarTarefa,
  criarColaborador,
  criarPosto,
  criarSetor,
  criarSubsetor,
  criarTarefa,
  definirPrazosAmbiente,
  definirPrazosPeca,
  desmembrarPeca,
  ehConflito,
  executarAcaoAmbiente,
  executarAcaoPeca,
  executarAcaoTarefa,
  excluirColaborador,
  excluirPosto,
  excluirSubsetor,
  salvarFormas,
} from "../../../services/mapaProducao";

const MENSAGEM_CONFLITO = "Alguém alterou isto antes de você. Recarregue.";

/**
 * Campos que identificam o ambiente em toda escrita. `versao` é a da linha
 * gravada (mesmo antiga, de outro setor); sem alocação válida, `status_atual`
 * diz ao backend de onde partir — a planilha pode dizer "em produção" antes
 * de qualquer apontamento no app.
 *
 * `etapa_origem` é a etapa da planilha em que o ambiente está AGORA: é ela que
 * faz a alocação morrer quando o PPCP digita a data e o ambiente anda, e é o
 * que sustenta "movido à mão" sem perder essa regra (`dados/combinar.js`).
 */
export function identificacaoDoAmbiente(demanda) {
  const base = {
    chave: demanda.chave,
    rotulo: demanda.rotulo,
    setor_id: demanda.setorId,
    etapa_origem: demanda.etapa || null,
    versao: demanda.versaoAlocacao ?? 0,
  };
  return demanda.alocacao ? base : { ...base, status_atual: demanda.statusApp };
}

function exigirChave(demanda) {
  if (!demanda?.chave) {
    throw new Error("Esta linha da planilha não tem pedido ou ambiente; complete a planilha para apontar.");
  }
}

/**
 * A edição da peça no painel mexe em campos de TRÊS endpoints diferentes, e o
 * backend ignora em silêncio o que não é do endereço dela:
 *
 * - `PATCH /pecas/{id}` aceita só `nome`, `prioridade` e `ambiente_rotulo`;
 * - responsável é alocação (`POST /pecas/{id}/alocar`, com `colaborador_id`);
 * - prazo é `POST /pecas/{id}/prazos` — e é o ÚNICO que registra o `motivo`.
 *
 * Mandar tudo no PATCH devolvia 200 com a peça intacta: o gestor trocava o
 * responsável e o prazo, via "Peça atualizada." e nada mudava. Cada escrita
 * sobe a versão, então a seguinte usa a versão que a anterior devolveu —
 * senão a segunda toma 409 da primeira.
 */
const CAMPOS_DO_PATCH = ["nome", "prioridade", "ambiente_rotulo"];
const CAMPOS_DE_PRAZO = ["data_prevista", "data_limite"];

function apenas(campos, permitidos) {
  const escolhidos = {};
  permitidos.forEach((campo) => {
    if (campo in campos) escolhidos[campo] = campos[campo];
  });
  return escolhidos;
}

export async function salvarCamposDaPeca({ request, peca, campos = {}, motivo = "" }) {
  const dados = apenas(campos, CAMPOS_DO_PATCH);
  const prazos = apenas(campos, CAMPOS_DE_PRAZO);
  const trocouResponsavel = "colaborador_id" in campos;
  let atual = peca;
  if (Object.keys(dados).length) {
    atual = (await atualizarPeca(request, peca.id, { ...dados, versao: atual.versao })) || atual;
  }
  if (trocouResponsavel) {
    atual = (await alocarPeca(request, peca.id, {
      colaborador_id: campos.colaborador_id,
      versao: atual.versao,
    })) || atual;
  }
  if (Object.keys(prazos).length) {
    atual = (await definirPrazosPeca(request, peca.id, {
      ...prazos,
      versao: atual.versao,
      ...(motivo ? { motivo } : {}),
    })) || atual;
  }
  return atual === peca ? null : atual;
}

/**
 * Devolve {conflito, limparConflito, alocar, alocarVarios, acaoAmbiente,
 * prazosAmbiente, criarTarefa, atualizarTarefa,
 * moverTarefa, acaoTarefa, comentarTarefa, desmembrarPeca, salvarPeca,
 * moverPeca, acaoPeca, comentarPeca, salvarSetor, salvarFormas,
 * salvarSubsetor, salvarColaborador, salvarPosto, excluirCadastro}.
 */
export default function useAcoesDoSetor({ request, estadoMapa }) {
  const [conflito, setConflito] = useState(null);
  const { aplicarAlocacao, aplicarTarefa, aplicarPeca, aplicarSetor, recarregar } = estadoMapa;

  // `lerDepois: false` = quem chama lê o estado uma vez no fim (lote).
  const escrever = useCallback(
    async (chamada, aplicar, { lerDepois = true } = {}) => {
      try {
        const resposta = await chamada();
        if (aplicar) aplicar(resposta);
        if (lerDepois) recarregar();
        return resposta;
      } catch (erro) {
        if (ehConflito(erro)) {
          setConflito(erro?.message || MENSAGEM_CONFLITO);
          // A versão que a tela tem está velha: já busca a nova, para a
          // próxima tentativa não bater no mesmo 409.
          recarregar();
        }
        throw erro;
      }
    },
    [recarregar]
  );

  // ------------------------------------------------------------ ambiente

  const alocarUm = useCallback(
    (demanda, { subsetorId, postoId }, opcoes) => {
      exigirChave(demanda);
      return escrever(
        () =>
          alocarAmbiente(request, {
            ...identificacaoDoAmbiente(demanda),
            subsetor_id: subsetorId || null,
            posto_id: postoId || null,
          }),
        aplicarAlocacao,
        opcoes
      );
    },
    [request, escrever, aplicarAlocacao]
  );
  const alocar = useCallback((demanda, destino) => alocarUm(demanda, destino), [alocarUm]);

  // Lote: um ambiente por vez; para no primeiro erro (o resto fica onde
  // está). Cada resposta entra na hora; o /estado é lido uma vez no fim.
  const alocarVarios = useCallback(
    async (demandas, destino) => {
      const comChave = demandas.filter((d) => d.chave);
      try {
        for (const demanda of comChave) {
          // eslint-disable-next-line no-await-in-loop
          await alocarUm(demanda, destino, { lerDepois: false });
        }
      } finally {
        recarregar();
      }
      return comChave.length;
    },
    [alocarUm, recarregar]
  );

  const acaoAmbiente = useCallback(
    (demanda, acao, motivo = "") => {
      exigirChave(demanda);
      return escrever(
        () => executarAcaoAmbiente(request, { ...identificacaoDoAmbiente(demanda), acao, motivo }),
        aplicarAlocacao
      );
    },
    [request, escrever, aplicarAlocacao]
  );

  const prazosAmbiente = useCallback(
    (demanda, { prevista, limite, prioridade, motivo = "" }) => {
      exigirChave(demanda);
      return escrever(
        () =>
          definirPrazosAmbiente(request, {
            ...identificacaoDoAmbiente(demanda),
            data_prevista: prevista || null,
            data_limite: limite || null,
            ...(prioridade ? { prioridade } : {}),
            motivo,
          }),
        aplicarAlocacao
      );
    },
    [request, escrever, aplicarAlocacao]
  );

  // -------------------------------------------------------------- tarefa

  const criar = useCallback(
    (dados) => escrever(() => criarTarefa(request, dados), aplicarTarefa),
    [request, escrever, aplicarTarefa]
  );

  const atualizar = useCallback(
    (tarefa, campos, motivo = "") =>
      escrever(
        () => atualizarTarefa(request, tarefa.id, { ...campos, versao: tarefa.versao, ...(motivo ? { motivo } : {}) }),
        aplicarTarefa
      ),
    [request, escrever, aplicarTarefa]
  );

  const moverTarefa = useCallback(
    (tarefa, { subsetorId, postoId }) =>
      atualizar(tarefa, { subsetor_id: subsetorId || null, posto_id: postoId || null }),
    [atualizar]
  );

  const acaoTarefa = useCallback(
    (tarefa, acao, motivo = "") =>
      escrever(
        () => executarAcaoTarefa(request, tarefa.id, { acao, versao: tarefa.versao, motivo }),
        aplicarTarefa
      ),
    [request, escrever, aplicarTarefa]
  );

  const comentarNaTarefa = useCallback(
    (tarefa, texto) => escrever(() => comentarTarefa(request, tarefa.id, texto)),
    [request, escrever]
  );

  // ---------------------------------------------------------------- peça

  /** `dados` já sai pronto de `herdarDoAmbiente` + o nome que o gestor digitou. */
  const desmembrar = useCallback(
    (dados) => escrever(() => desmembrarPeca(request, dados), aplicarPeca),
    [request, escrever, aplicarPeca]
  );

  const salvarPeca = useCallback(
    (peca, campos, motivo = "") => escrever(() => salvarCamposDaPeca({ request, peca, campos, motivo }), aplicarPeca),
    [request, escrever, aplicarPeca]
  );

  /** A peça anda sozinha pelos setores: o destino pode ser outro setor. */
  const moverPeca = useCallback(
    (peca, { setorId, subsetorId, postoId }) =>
      escrever(
        () =>
          alocarPeca(request, peca.id, {
            setor_id: setorId || peca.setorId || null,
            subsetor_id: subsetorId || null,
            posto_id: postoId || null,
            versao: peca.versao,
          }),
        aplicarPeca
      ),
    [request, escrever, aplicarPeca]
  );

  const acaoPeca = useCallback(
    (peca, acao, motivo = "") =>
      escrever(() => executarAcaoPeca(request, peca.id, { acao, versao: peca.versao, motivo }), aplicarPeca),
    [request, escrever, aplicarPeca]
  );

  const comentarNaPeca = useCallback(
    (peca, texto) => escrever(() => comentarPeca(request, peca.id, texto)),
    [request, escrever]
  );

  // ------------------------------------------------------------ cadastros

  /** Setor: `dados` com `versao` quando é edição (é a trava otimista). */
  const salvarSetor = useCallback(
    (id, dados) => escrever(() => (id ? atualizarSetor(request, id, dados) : criarSetor(request, dados)), aplicarSetor),
    [request, escrever, aplicarSetor]
  );

  /**
   * Desenho de vários elementos de uma vez (tudo ou nada no backend). A
   * resposta traz as versões novas; o /estado é lido em seguida para a tela
   * não continuar com a versão velha na mão.
   */
  const salvarDesenho = useCallback((formas) => escrever(() => salvarFormas(request, formas)), [request, escrever]);

  const salvarSubsetor = useCallback(
    (id, dados) => escrever(() => (id ? atualizarSubsetor(request, id, dados) : criarSubsetor(request, dados))),
    [request, escrever]
  );
  const salvarColaborador = useCallback(
    (id, dados) =>
      escrever(() => (id ? atualizarColaborador(request, id, dados) : criarColaborador(request, dados))),
    [request, escrever]
  );
  const salvarPosto = useCallback(
    (id, dados) => escrever(() => (id ? atualizarPosto(request, id, dados) : criarPosto(request, dados))),
    [request, escrever]
  );

  /**
   * Apaga de vez subsetor, posto ou colaborador — do desenho ou da lista do
   * Configurar. É o desfazer de quem criou errado: o backend só deixa apagar
   * o que NUNCA carregou trabalho e recusa o resto com a frase que a tela
   * mostra ("desative em vez de excluir"). Depois de apagar, relê o estado —
   * a linha sumiu do servidor e mesclar a resposta vazia deixaria um
   * fantasma na tela.
   */
  const excluirCadastro = useCallback(
    (alvo, item) =>
      escrever(async () => {
        if (alvo === "colaborador") await excluirColaborador(request, item.id);
        else if (alvo === "posto") await excluirPosto(request, item.id, item.versao);
        else await excluirSubsetor(request, item.id, item.versao);
        await recarregar?.();
        return null;
      }),
    [request, escrever, recarregar]
  );

  const limparConflito = useCallback(() => setConflito(null), []);

  return useMemo(
    () => ({
      conflito,
      limparConflito,
      alocar,
      alocarVarios,
      acaoAmbiente,
      prazosAmbiente,
      criarTarefa: criar,
      atualizarTarefa: atualizar,
      moverTarefa,
      acaoTarefa,
      comentarTarefa: comentarNaTarefa,
      desmembrarPeca: desmembrar,
      salvarPeca,
      moverPeca,
      acaoPeca,
      comentarPeca: comentarNaPeca,
      salvarSetor,
      salvarFormas: salvarDesenho,
      excluirCadastro,
      salvarSubsetor,
      salvarColaborador,
      salvarPosto,
    }),
    [
      conflito, limparConflito, alocar, alocarVarios, acaoAmbiente, prazosAmbiente,
      criar, atualizar, moverTarefa, acaoTarefa, comentarNaTarefa, desmembrar, salvarPeca, moverPeca,
      acaoPeca, comentarNaPeca, salvarSetor, salvarDesenho, salvarSubsetor, salvarColaborador, salvarPosto,
      excluirCadastro,
    ]
  );
}
